import { Alert, Button, Grid, Typography } from "@mui/material"
import { crudGetOne, ListContextProvider, ReduxState } from "ra-core"
import { FC, useEffect, useMemo } from "react"
import { Content } from "@/components/content/Content"
import { OdResourcePageProps } from "@/types/resource"
import LinkIcon from '@mui/icons-material/Link';
import { Link as RouterLink } from 'react-router-dom'
import { RecPatch } from "@/dataProvider/tools"
import { NetworkConnectionResponseDto } from "@/gen/api/deploy"
import useLocalListController from "@/components/list/useLocalListController"
import { useDispatch, useSelector } from "react-redux"
import { getStashedDeployInfo } from "@/reducers/deployStash"
import NetworkConnectTable from "@/components/deployment/NetworkConnectionTable"
import { NetworkConfigurationContextProvider, useNetworkConfigurationController } from "./useNetworkConfigurationContext"
import NetworkGatewayInfo, { NetworkConfigureContext } from "@/components/deployment/NetworkGatewayInfo"
import { useResourceRegister } from "@/utils/resourceRegister"
import { CloudSubscriptionResponseDto } from "@/gen/api/user"
import { crudGetOneEx } from "@/actions/crudFetchEx"


const networkListOptions : {sorters: Record<string, (a:NetworkConnectionResponseDto & RecPatch,b:NetworkConnectionResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => (a.networkConnectionId || 0) < (b.networkConnectionId || 0) ? -1 : 1,
    name: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
  }
}

const resource = 'settings/deployment/network-configuration'

const NetworkConfigurationList: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: 'network-connection-sku' })
  useResourceRegister({ name: 'random-key' })

  const ctrls = useNetworkConfigurationController()

  return (
    <NetworkConfigurationContextProvider value={ctrls}>
      <NetworkConfigurationListBody {...props} />
    </NetworkConfigurationContextProvider>
  )
}

export interface NetworkConfigurationListBodyProps extends OdResourcePageProps {
}

const cloudSubscriptionRes = 'settings/organization/cloud-subscription'

const NetworkConfigurationListBody: FC<NetworkConfigurationListBodyProps> = ({
  options,
  resource,
}) => {
  const dispatch = useDispatch()
  const cloudSubs = useSelector<ReduxState, CloudSubscriptionResponseDto&RecPatch | undefined>(store => store.admin.resources[cloudSubscriptionRes]?.data[1])
  useEffect(() => {
    dispatch(crudGetOneEx(cloudSubscriptionRes, 1, ''))
  }, [dispatch])
  const ctxVal = useMemo(() => ({
    shouldSuspendUsage: !!(cloudSubs?.isEnabled)
  }), [cloudSubs?.isEnabled])
  
  const stashedDeployInfo = useSelector(getStashedDeployInfo)
  
  const listCtrls = useLocalListController({ resource: 'network-connections', basePath: '' }, networkListOptions)

  return (
    <Content 
      pageName="Deployment Settings" 
      resourceName="Deployment Settings" 
      breadcrumbs={options?.breadcrumbs}
      secondaryActions={
        stashedDeployInfo &&
        <Button
          color="primary"
          startIcon={<LinkIcon fontSize="small" />}
          component={RouterLink}
          to={{
            pathname: '/deployments/list',
            state: { openCreate: true }
          }}
        >
          Back to Deploy Database
        </Button>
      }
    >
      <NetworkConfigureContext.Provider value={ctxVal}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {ctxVal.shouldSuspendUsage &&
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography
                  variant="body1"
                >
                  Since your organization has "Use My Cloud Subscription" option turned on, you will use network infrastructure provisioned in your organization's Cloud Subscription. 
                  If you have any network resource configured here, it will no longer be used. It's usually a good practice to clean up resources you no longer need.
                </Typography>
              </Alert>
            </Grid>}
          <Grid item xs={12}>
            <NetworkGatewayInfo />
          </Grid>
          <Grid item xs={12}>
            <ListContextProvider value={listCtrls} >
              <NetworkConnectTable />
            </ListContextProvider>
          </Grid>
        </Grid>
      </NetworkConfigureContext.Provider>

    </Content>
  )
}




export default NetworkConfigurationList;
